import React, { useCallback, useEffect, useState } from 'react';

// Navigation things
import { Link } from '@reach/router';

// Presentation things
import { Accordion } from '../../../components/Accordion';
import { Table } from '../../../components/Table';

// Data things
import { ExpiredAccommodationListItem } from '../../../contexts/Accommodations/helper';
import { IExpiredAccommodationListDTO } from '../../../typings/DTOs';
import { ApiErrorResult, api } from '../../../utils/api';
import { PaginatedList } from '../../../typings/common';
import { MEDIUM_SCREEN } from '../../../constants';
import { toDateString } from '../../../constants/functions';
import { useIsMounted } from '../../../hooks';

const header = ['Munkavállaló', 'Szálláshely', 'Beköltözés', 'Kiköltözés', 'Szerződés lejárata', ''];

const ExpiredAccommodations = () => {
  /* States */
  const [accommodations, setAccommodations] = useState<PaginatedList<ExpiredAccommodationListItem>>({ items: [], error: '', isLoading: true, offset: 0, page: 1, totalLength: 0 });

  /* Hooks */
  const _isMounted = useIsMounted();

  const onGetAccommodations = useCallback(
    async (search: string = '', signal?: AbortSignal, page: number = 1): Promise<string | null> => {
      try {
        setAccommodations((prev) => ({ ...prev, error: '', isLoading: true, page }));

        const resp = await api<IExpiredAccommodationListDTO>('accommodation/expired', { search, limit: 50, offset: (page - 1) * 50 }, { signal });

        if (!resp.message && _isMounted.current) {
          if (!resp.items.length) throw new Error('Nincsenek beköltözések szállás szerződés nélkül');
          setAccommodations((p) => ({ ...p, items: resp.items.map((x) => new ExpiredAccommodationListItem(x)), totalLength: resp.totalLength, offset: resp.offset, isLoading: false }));
          return Promise.resolve(null);
        } else throw new Error(resp.message);
      } catch (error) {
        const { message } = error as ApiErrorResult;
        if (_isMounted.current) {
          setAccommodations((p) => ({ ...p, items: [], error: String(message || error), isLoading: false }));
        }
        return Promise.reject(message);
      }
    },
    [_isMounted]
  );

  useEffect(() => void onGetAccommodations(), [onGetAccommodations]);

  return (
    <>
      <div className="table-header flex-row items-center">
        <h3>Beköltözések szállás szerződés nélkül</h3>
      </div>

      <Table
        className="expired-dashboard-table"
        maxDataCount={accommodations.totalLength}
        loading={accommodations.isLoading}
        error={accommodations.error}
        data={accommodations.items}
        page={accommodations.page}
        header={header}
        onChangePage={onGetAccommodations}
        onSearch={onGetAccommodations}
        onRowRender={TableRow}
      />
    </>
  );
};

const Row = (row: ExpiredAccommodationListItem, gridTemplate: React.CSSProperties) => (
  <div className="data-table__body-row" style={gridTemplate} key={row.employee.id}>
    <div>
      <Link title={row.employee.name} to={`/munkavallalok/${row.employee.id}`} className="hot-link w-full">
        {row.employee.name}
      </Link>

      <span>{row.employee.idNumbers}</span>
    </div>

    <div>
      <Link title={row.accommodation.address} to={`/szallasok/${row.accommodation.id}`} className="hot-link w-full">
        {row.accommodation.address}
      </Link>
    </div>

    <div>{toDateString(row.moveInDate)}</div>

    <div>{toDateString(row.moveOutDate)}</div>

    <div>{toDateString(row.lastContractEndDate)}</div>
  </div>
);

const MobileRow = (row: ExpiredAccommodationListItem) => (
  <Accordion
    key={row.employee.id}
    isCard
    isBorderBottom
    className="card"
    summaryClassName="h-auto"
    label={
      <div className="card-header twsm:flex-row twsm:justify-between twsm:items-center">
        <div className="flex flex-col items-start">
          <Link title={row.employee.name} to={`/munkavallalok/${row.employee.id}`} className="card-header__primary-text hot-link">
            {row.employee.name}
          </Link>
          <span className="card-header__secondary-text">{row.employee.idNumbers}</span>
        </div>

        <div className="data border-t-0 mt-0 twsm:min-w-[160px]">
          <div className="data-item gap-4">
            <span className="data-item__title">Szerződés lejárata</span>

            <span className="data-item__data">{toDateString(row.lastContractEndDate)}</span>
          </div>
        </div>
      </div>
    }>
    <div className="data">
      <div className="data-item">
        <span className="data-item__title">Szálláshely</span>

        <span className="data-item__data">
          <Link title={row.accommodation.address} to={`/szallasok/${row.accommodation.id}`} className="hot-link">
            {row.accommodation.address}
          </Link>
        </span>
      </div>

      <div className="data-item">
        <span className="data-item__title">Beköltözés</span>

        <span className="data-item__data">{toDateString(row.moveInDate)}</span>
      </div>

      <div className="data-item">
        <span className="data-item__title">Kiköltözés</span>

        <span className="data-item__data">{toDateString(row.moveOutDate)}</span>
      </div>
    </div>
  </Accordion>
);

const TableRow = { default: Row, mobile: MobileRow, breakpoint: MEDIUM_SCREEN };

export default ExpiredAccommodations;
